import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TabsContent } from "@/components/ui/tabs";
import { errorHandler } from "@/lib/utils";
import React, { useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { toast } from "sonner";
import { AccessLevelFormValues } from "./form";

interface AccessLevelModulesTabProps {
  form: UseFormReturn<AccessLevelFormValues>;
  fields: UseFieldArrayReturn<AccessLevelFormValues, "modules">["fields"];
  isAdmin?: boolean;
}

interface Module {
  module_id: number;
  module_title: string;
  permission: number;
  max_permission: number;
  parent_id?: number | null;
  children?: Module[];
}

const organizeModules = (modules: Module[]) => {
  const moduleMap: { [key: number]: Module } = {};

  modules.forEach((module) => {
    moduleMap[module.module_id] = {
      ...module,
      children: [],
    };
  });

  const rootModules: Module[] = [];

  modules.forEach((module) => {
    if (module.parent_id && moduleMap[module.parent_id]) {
      moduleMap[module.parent_id].children?.push(moduleMap[module.module_id]);
    } else {
      rootModules.push(moduleMap[module.module_id]);
    }
  });

  return rootModules;
};

const AccessLevelModulesTab = ({
  form,
  fields,
  isAdmin,
}: AccessLevelModulesTabProps) => {
  const [selectAll, setSelectAll] = useState(false);
  const organizedModules = organizeModules(fields);

  const applyPermissionToChildren = (module: Module, permission: number) => {
    module.children?.forEach((child) => {
      const childIndex = fields.findIndex(
        (f) => f.module_id === child.module_id,
      );

      if (childIndex !== -1) {
        const clampedPermission = permission & child.max_permission;

        form.setValue(`modules.${childIndex}.permission`, clampedPermission, {
          shouldDirty: true,
        });
      }

      if (child.children && child.children.length > 0) {
        applyPermissionToChildren(child, permission);
      }
    });
  };

  const handleSelectAll = async (checked: boolean) => {
    if (isAdmin) return; // Admin herda do client, não editável aqui
    setSelectAll(checked);

    try {
      fields.forEach((module, index) => {
        const newValue = checked ? module.max_permission : 0;
        form.setValue(`modules.${index}.permission`, newValue, {
          shouldDirty: true,
        });
      });
      toast.success("Todas as permissões foram atualizadas (ainda não salvas)");
    } catch (err) {
      errorHandler(err, "Erro ao atualizar permissões");
    }
  };

  const handlePermissionChange = async (
    moduleIndex: number,
    _moduleId: number,
    newPermission: number,
  ) => {
    if (isAdmin) return; // Admin herda do client, não editável aqui
    try {
      form.setValue(`modules.${moduleIndex}.permission`, newPermission, {
        shouldDirty: true,
      });
    } catch (err) {
      errorHandler(err, "Erro ao atualizar permissão do módulo");
    }
  };

  const renderModuleRow = (module: Module, level: number = 0) => {
    const moduleIndex = fields.findIndex(
      (f) => f.module_id === module.module_id,
    );

    if (moduleIndex === -1) return null;

    return (
      <React.Fragment key={module.module_id}>
        <TableRow className={`${level > 0 ? "" : "bg-muted/95"}`}>
          <TableCell className={`${level > 0 ? "pl-8" : "font-bold"}`}>
            {module.module_title}
          </TableCell>
          <TableCell>
            {module.max_permission & 2 ? (
              <FormField
                control={form.control}
                name={`modules.${moduleIndex}.permission`}
                render={({ field: { value = 0 } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        className="bg-foreground"
                        checked={!!(value & 2)}
                        onCheckedChange={(checked) => {
                          const newValue = checked ? value | 2 : value & ~2;
                          handlePermissionChange(
                            moduleIndex,
                            module.module_id,
                            newValue,
                          );

                          if (module.children && module.children.length > 0) {
                            applyPermissionToChildren(module, newValue);
                          }
                        }}
                        disabled={isAdmin || !(module.max_permission & 2)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : (
              <div
                className="h-6 w-10 bg-muted rounded-full opacity-20 cursor-not-allowed"
                title="Não disponível para o cliente"
              />
            )}
          </TableCell>
          <TableCell>
            {module.max_permission & 4 ? (
              <FormField
                control={form.control}
                name={`modules.${moduleIndex}.permission`}
                render={({ field: { value = 0 } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        className="bg-foreground"
                        checked={!!(value & 4)}
                        onCheckedChange={(checked) => {
                          const newValue = checked ? value | 4 : value & ~4;
                          handlePermissionChange(
                            moduleIndex,
                            module.module_id,
                            newValue,
                          );

                          if (module.children && module.children.length > 0) {
                            applyPermissionToChildren(module, newValue);
                          }
                        }}
                        disabled={isAdmin || !(module.max_permission & 4)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : (
              <div
                className="h-6 w-10 bg-muted rounded-full opacity-20 cursor-not-allowed"
                title="Não disponível para o cliente"
              />
            )}
          </TableCell>
          <TableCell>
            {module.max_permission & 8 ? (
              <FormField
                control={form.control}
                name={`modules.${moduleIndex}.permission`}
                render={({ field: { value = 0 } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        className="bg-foreground"
                        checked={!!(value & 8)}
                        onCheckedChange={(checked) => {
                          const newValue = checked ? value | 8 : value & ~8;
                          handlePermissionChange(
                            moduleIndex,
                            module.module_id,
                            newValue,
                          );

                          if (module.children && module.children.length > 0) {
                            applyPermissionToChildren(module, newValue);
                          }
                        }}
                        disabled={isAdmin || !(module.max_permission & 8)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : (
              <div
                className="h-6 w-10 bg-muted rounded-full opacity-20 cursor-not-allowed"
                title="Não disponível para o cliente"
              />
            )}
          </TableCell>
          <TableCell>
            {module.max_permission & 16 ? (
              <FormField
                control={form.control}
                name={`modules.${moduleIndex}.permission`}
                render={({ field: { value = 0 } }) => (
                  <FormItem>
                    <FormControl>
                      <Switch
                        className="bg-foreground"
                        checked={!!(value & 16)}
                        onCheckedChange={(checked) => {
                          const newValue = checked ? value | 16 : value & ~16;
                          handlePermissionChange(
                            moduleIndex,
                            module.module_id,
                            newValue,
                          );

                          if (module.children && module.children.length > 0) {
                            applyPermissionToChildren(module, newValue);
                          }
                        }}
                        disabled={isAdmin || !(module.max_permission & 16)}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            ) : (
              <div
                className="h-6 w-10 bg-muted rounded-full opacity-20 cursor-not-allowed"
                title="Não disponível para o cliente"
              />
            )}
          </TableCell>
        </TableRow>
        {module.children?.map((childModule) =>
          renderModuleRow(childModule, level + 1),
        )}
      </React.Fragment>
    );
  };

  return (
    <TabsContent value="modules" className="space-y-6 mt-4">
      <div className="space-y-4">
        <div className="p-4 rounded-md">
          {isAdmin && (
            <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-sm text-blue-700 dark:text-blue-300">
              Este é um perfil de administrador. Ele herda automaticamente todas
              as permissões configuradas para o cliente e não pode ser editado
              manualmente.
            </div>
          )}
          <div className="flex items-center mb-4">
            <label htmlFor="select-all" className="mr-2">
              Permitir tudo
            </label>
            <Switch
              id="select-all"
              className="bg-foreground"
              checked={selectAll}
              onCheckedChange={handleSelectAll}
              disabled={isAdmin}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Módulo</TableHead>
                <TableHead>Visualizar</TableHead>
                <TableHead>Cadastrar</TableHead>
                <TableHead>Alterar</TableHead>
                <TableHead>Excluir</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {organizedModules.map((module) => renderModuleRow(module))}
            </TableBody>
          </Table>
        </div>
      </div>
    </TabsContent>
  );
};

export default AccessLevelModulesTab;
